import { Tracker } from '../../analytics/services/Tracker';
import {
  PROCESS,
  PROCESS_DOC,
  PROCESS_EXTRACTION,
} from '../../analytics/constants/MetricEvents';

/**
 * Typed facade over Tracker.track for the Process domain (mapping +
 * BPMN + AI + extraction). Same pattern as CompassTracking — one
 * function per declared `metric.process.*` event.
 *
 * Wired call sites:
 *   - ProcessCanvasPlugin / ProcessMappingAssistantPanel (diagram
 *     create, ai generation, analysis)
 *   - process-doc-jobs mutations (extraction job complete, audit trail)
 *
 * Events that are computed (rates, averages — `aggregated` /
 * `derived` strategies) live in the registry but never have a direct
 * Tracker.track call — they are derived from the events below.
 *
 * Wired call sites (May 2026):
 *   - FlowchartCanvasPlugin.ts — diagramCreated (Yjs-dedup first-open)
 *   - ApplyAutoLayoutTool.tsx — autoLayoutApplied
 *   - ExportControls.tsx — bpmnExported
 *   - import-review/hooks/useJobs.ts — extractionJobCompleted
 *   - import-review/components/audit-trail/AuditTrailTab.tsx — extractionAuditTrailViewed
 *
 * Pending wire-up:
 *   - returnUsage (needs session/visit telemetry)
 *   - stepInserted (InsertStepBetweenCommand / InsertStepTool)
 *   - bpmnImported, bpmnDiagramCreated, subProcessLinked, drillDownUsed,
 *     aiGenerated, analysisRun, patternActionApplied — surfaces exist
 *     but no Tracker call yet; wire as each feature ships measurement
 *     review per `.github/EPIC_CHECKLIST.md`.
 *   - extractionFindingResolved, extractionImagePdfRejected — server
 *     side or specific UI flows yet to land.
 */
export const ProcessTracking = {
  diagramCreated(props: { diagramId?: string; firstSeenAt: string }): void {
    Tracker.track(PROCESS.DIAGRAM_CREATED, props);
  },
  returnUsage(props: { diagramId?: string; daysSinceLastVisit: number }): void {
    Tracker.track(PROCESS.RETURN_USAGE, props);
  },
  stepInserted(props: { diagramId?: string; insertedById: string; position: 'before' | 'after' }): void {
    Tracker.track(PROCESS.STEP_INSERTED, props);
  },
  autoLayoutApplied(props: { diagramId?: string; nodeCount: number; durationMs?: number }): void {
    Tracker.track(PROCESS.AUTO_LAYOUT_APPLIED, props);
  },
  bpmnDiagramCreated(props: { diagramId: string }): void {
    Tracker.track(PROCESS.BPMN_DIAGRAM_CREATED, props);
  },
  bpmnExported(props: { diagramId?: string; format: string; sizeBytes: number }): void {
    Tracker.track(PROCESS.BPMN_EXPORTED, props);
  },
  bpmnImported(props: { diagramId?: string; nodeCount: number; sourceFormat: string }): void {
    Tracker.track(PROCESS.BPMN_IMPORTED, props);
  },
  subProcessLinked(props: { diagramId?: string; parentNodeId: string; childDiagramId: string }): void {
    Tracker.track(PROCESS.SUB_PROCESS_LINKED, props);
  },
  drillDownUsed(props: { diagramId?: string; fromNodeId: string; toDiagramId: string }): void {
    Tracker.track(PROCESS.DRILL_DOWN_USED, props);
  },
  aiGenerated(props: { diagramId?: string; nodeCount: number; source: 'prompt' | 'document' | 'append' }): void {
    Tracker.track(PROCESS.AI_GENERATED, props);
  },
  analysisRun(props: { diagramId?: string; patternsDetected: number }): void {
    Tracker.track(PROCESS.ANALYSIS_RUN, props);
  },
  patternActionApplied(props: { diagramId?: string; patternId: string; actionId: string }): void {
    Tracker.track(PROCESS.PATTERN_ACTION_APPLIED, props);
  },
  granularSuggestionCreated(props: {
    folderId: string;
    subjectKind: 'field' | 'statement' | 'list-item' | 'relationship';
    operation: 'add' | 'replace' | 'remove';
  }): void {
    Tracker.track(PROCESS.GRANULAR_SUGGESTION_CREATED, props);
  },
  granularSuggestionReviewed(props: {
    folderId: string;
    action: 'accept' | 'reject' | 'revise';
  }): void {
    Tracker.track(PROCESS.GRANULAR_SUGGESTION_REVIEWED, props);
  },
  granularSuggestionStale(props: {
    folderId: string;
    action: 'accept' | 'revise';
  }): void {
    Tracker.track(PROCESS.GRANULAR_SUGGESTION_STALE, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Extraction (PDF → graph pipeline)
  // ──────────────────────────────────────────────────────────────────
  extractionJobCompleted(props: { jobId: string; durationMs: number; processesGenerated: number; faithfulnessPct?: number }): void {
    Tracker.track(PROCESS_EXTRACTION.JOB_COMPLETED, props);
  },
  extractionAuditTrailViewed(props: { jobId: string }): void {
    Tracker.track(PROCESS_EXTRACTION.AUDIT_TRAIL_VIEWED, props);
  },
  extractionFindingResolved(props: { jobId: string; findingId: string }): void {
    Tracker.track(PROCESS_EXTRACTION.FINDING_RESOLVED, props);
  },
  extractionImagePdfRejected(props: { jobId: string; sourceFileName?: string }): void {
    Tracker.track(PROCESS_EXTRACTION.IMAGE_PDF_REJECTED, props);
  },
  extractionSourceFidelityViewed(props: {
    jobId: string;
    folderId?: string | null;
    status?: string | null;
    failedGateCount: number;
    blockedProjectionEntries: number;
    sourceUnits: number;
    claims: number;
  }): void {
    Tracker.track(PROCESS_EXTRACTION.SOURCE_FIDELITY_VIEWED, props);
  },
  extractionSourceFidelityIssueSurfaced(props: {
    folderId: string;
    issueCount: number;
    jobIds: string[];
    failedGateCount: number;
    blockedProjectionEntries: number;
  }): void {
    Tracker.track(PROCESS_EXTRACTION.SOURCE_FIDELITY_ISSUE_SURFACED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Process-doc job lifecycle (Import Review UI)
  // ──────────────────────────────────────────────────────────────────
  jobCreated(props: { jobId: string; sourceType: string; sourceDocumentCount?: number }): void {
    Tracker.track(PROCESS_DOC.JOB_CREATED, props);
  },
  jobDispatched(props: { jobId: string; stage?: string }): void {
    Tracker.track(PROCESS_DOC.JOB_DISPATCHED, props);
  },
  jobCancelled(props: { jobId: string; reason?: string }): void {
    Tracker.track(PROCESS_DOC.JOB_CANCELLED, props);
  },
  jobPublished(props: { jobId: string }): void {
    Tracker.track(PROCESS_DOC.JOB_PUBLISHED, props);
  },
  jobApplied(props: { jobId: string; folderId?: string }): void {
    Tracker.track(PROCESS_DOC.JOB_APPLIED, props);
  },
  planApproved(props: { jobId: string }): void {
    Tracker.track(PROCESS_DOC.PLAN_APPROVED, props);
  },
  planRevised(props: { jobId: string; feedbackLength: number; hasOverrides: boolean }): void {
    Tracker.track(PROCESS_DOC.PLAN_REVISED, props);
  },
  reviewDecisionRecorded(props: {
    jobId: string;
    targetType: string;
    targetId: string;
    decision: string;
  }): void {
    Tracker.track(PROCESS_DOC.REVIEW_DECISION_RECORDED, props);
  },
  enrichmentStarted(props: { jobId: string; processCount: number; budgetUsd?: number }): void {
    Tracker.track(PROCESS_DOC.ENRICHMENT_STARTED, props);
  },
  enrichmentSkipped(props: { jobId: string; reason?: string }): void {
    Tracker.track(PROCESS_DOC.ENRICHMENT_SKIPPED, props);
  },
  enrichmentRetried(props: { jobId: string; canonicalProcessId: string }): void {
    Tracker.track(PROCESS_DOC.ENRICHMENT_RETRIED, props);
  },
  artifactsBrowsed(props: { jobId: string; artifactKind?: string }): void {
    Tracker.track(PROCESS_DOC.ARTIFACTS_BROWSED, props);
  },
} as const;
